import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  CircularProgress,
  Tooltip,
  Button,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import dayjs from "dayjs";
import axiosClient from "../api/axiosClient";
import EventDetailsDialog from "./EventDetailDialog.tsx";
import EventEditDialog from "./EventEditDialog.tsx";
import { CalendarEvent, CalendarEventFormData } from "../api/types.tsx";
import { calendarService, liveMatchService } from "../api/service.ts";
import { useAuth } from "../AuthContext.tsx";
import MatchResultDialog from "./MatchResult/MatchResultDialog.tsx";
import LiveMatchDialog from "./LiveMatch/LiveMatchDialog.tsx";
import { hasActiveSession } from "./LiveMatch/LiveMatchContext.tsx";

interface Props {
  open: boolean;
  onClose: () => void;
  tournamentId: number | null;
}

/** Messaggio d'errore del BE: testo semplice (400 di validazione) oppure oggetto con message */
function errorText(error: any, fallback: string): string {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  return data?.message || error?.message || fallback;
}

// partite giocabili: di torneo e amichevoli
const isGame = (type?: string) => type === "TOURNAMENT" || type === "FRIENDLY";

export default function CalendarDialog({ open, onClose, tournamentId }: Props) {
  const { isAdmin } = useAuth();
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [hasLiveSession, setHasLiveSession] = useState(false);

  const [openMatchResultDialog, setOpenMatchResultDialog] = useState(false);
  const [matchForResult, setMatchForResult] = useState<CalendarEvent>();

  const [liveMatchEvent, setLiveMatchEvent] = useState<CalendarEvent | null>(null);

  const [openEditDateDialog, setOpenEditDateDialog] = useState(false);
  const [newDate, setNewDate] = useState<Date | null>(null);

  // State for event edit dialog (new event)
  const [openNewEventDialog, setOpenNewEventDialog] = useState(false);
  const [newEventDate, setNewEventDate] = useState<string>("");

  // State for event edit dialog (edit existing event)
  const [openEditEventDialog, setOpenEditEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);

  useEffect(() => {
    if (!selectedEvent || !isGame(selectedEvent.type) || selectedEvent.played) {
      setHasLiveSession(false);
      return;
    }
    liveMatchService.getByEvent(selectedEvent.eventId)
      .then((session) => setHasLiveSession(session !== null))
      .catch(() => setHasLiveSession(false));
  }, [selectedEvent]);

  const loadEvents = () => {
    setLoading(true);
    axiosClient
      .get(`/calendar`)
      .then((res) => {
        const mapped = res.data.map((e: CalendarEvent) => {
          let emoji = "";
          let color = "";

          switch (e.type) {
            case "TOURNAMENT":
              emoji = "🏐";
              color = "#ff7043";
              break;
            case "FRIENDLY":
              emoji = "🤝";
              color = "#26a69a";
              break;
            case "TRAINING":
              emoji = "💪";
              color = "#42a5f5";
              break;
            case "VISITA_MEDICA":
              emoji = "🩺";
              color = "#66bb6a";
              break;
            case "BIRTHDAY":
              emoji = "🎁";
              color = "#fdd835";
              break;
            default:
              emoji = "📅";
              color = "#9e9e9e";
          }

          if (isGame(e.type) && e.played) {
            color = "#c7a5deff";
          }

          return {
            id: `event-${e.eventId}-${e.date}-${e.time}`, // ID univoco per evitare conflitti
            title: `${emoji} ${e.title}${e.result ? " (" + e.result + ")" : ""}`,
            start: e.date,
            extendedProps: e,
            backgroundColor: color,
            borderColor: color,
          };
        });

        setEvents(mapped);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    loadEvents();
  }, [open, tournamentId]);

  const handleEventClick = (info: any) => {
    const ev = info.event.extendedProps as CalendarEvent;

    // CTRL (Windows/Linux) o CMD (Mac) + click -> edit date for TOURNAMENT
    if (info.jsEvent.ctrlKey || info.jsEvent.metaKey) {
      if (!isGame(ev.type)) return;
      setSelectedEvent(ev);
      setNewDate(new Date(ev.date));
      setOpenEditDateDialog(true);
      return;
    } else {
      // click normale -> show details
      setSelectedEvent(ev);
    }
  };

  const handleDateClick = (info: any) => {
    // Click on empty date -> open dialog to create new event
    const clickedDate = info.dateStr;
    setNewEventDate(clickedDate);
    setOpenNewEventDialog(true);
  };

  const handleSaveNewDate = async () => {
    if (!selectedEvent || !newDate) return;

    let dateStr: string;

    if (newDate instanceof Date) {
      dateStr = newDate.toISOString().split("T")[0];
    } else if (typeof newDate === "string") {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
        const parsed = new Date(newDate);
        if (isNaN(parsed.getTime())) {
          throw new Error("Formato data non valido. Usa yyyy-MM-dd oppure un oggetto Date.");
        }
        dateStr = parsed.toISOString().split("T")[0];
      } else {
        dateStr = newDate;
      }
    } else {
      throw new Error("Unsupported date type");
    }

    await axiosClient.put(`/calendar/${selectedEvent.eventId}/date`, {
      date: dateStr,
    });

    setOpenEditDateDialog(false);
    setSelectedEvent(null);
    loadEvents();
  };

  const handleCreateEvent = async (formData: CalendarEventFormData) => {
    try {
      await calendarService.createEvent(formData);
      setOpenNewEventDialog(false);
      setNewEventDate("");
      loadEvents();
    } catch (error: any) {
      console.error("Error creating event:", error);
      alert(errorText(error, "Errore nella creazione dell'evento"));
    }
  };

  const handleUpdateEvent = async (formData: CalendarEventFormData) => {
    if (!editingEvent) return;
    try {
      await calendarService.updateEvent(editingEvent.eventId, formData);
      setOpenEditEventDialog(false);
      setEditingEvent(null);
      loadEvents();
    } catch (error: any) {
      console.error("Error updating event:", error);
      alert(errorText(error, "Errore nell'aggiornamento dell'evento"));
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    try {
      await calendarService.deleteEvent(selectedEvent.eventId);
      setSelectedEvent(null);
      loadEvents();
    } catch (error: any) {
      console.error("Error deleting event:", error);
      alert(error.response?.data?.message || error.message || "Errore nell'eliminazione dell'evento");
    }
  };

  const canEditEvent = (event: CalendarEvent): boolean => {
    // TRAINING autogenerati (eventId === 0) non sono modificabili
    if (event.type === "TRAINING" && event.eventId === 0) {
      return false;
    }
    return true;
  };

  const canDeleteEvent = (event: CalendarEvent): boolean => {
    // TRAINING autogenerati, BIRTHDAY, VISITA_MEDICA non sono eliminabili
    if (event.type === "TRAINING" && event.eventId === 0) {
      return false;
    }
    if (event.type === "BIRTHDAY") {
      return false;
    }
    if (event.type === "VISITA_MEDICA") {
      return false;
    }
    return true;
  };

  const handleEditFromDetails = () => {
    if (selectedEvent) {
      setEditingEvent(selectedEvent);
      setOpenEditEventDialog(true);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ backgroundColor: "primary.main" }}>
          Calendar
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ height: 600 }}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </Box>
          ) : (
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              locale="it"
              events={events}
              height="100%"
              eventClick={handleEventClick}
              dateClick={handleDateClick}
              eventContent={(arg) => (
                <Tooltip title={arg.event.title} arrow placement="top">
                  <Box
                    sx={{
                      fontSize: "0.85rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {arg.event.title}
                  </Box>
                </Tooltip>
              )}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "",
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {selectedEvent && !openEditDateDialog && !openEditEventDialog && (
        <EventDetailsDialog
          open={!!selectedEvent}
          onClose={() => setSelectedEvent(null)}
          event={selectedEvent}
          onInsertResult={() => {
            setMatchForResult(selectedEvent);
            setOpenMatchResultDialog(true);
          }}
          onEdit={canEditEvent(selectedEvent) ? handleEditFromDetails : undefined}
          onDelete={canDeleteEvent(selectedEvent) ? handleDeleteEvent : undefined}
          onPlayNow={isAdmin && ((selectedEvent.type === 'TOURNAMENT' && selectedEvent.teamAId && selectedEvent.teamBId) || selectedEvent.type === 'FRIENDLY')
            ? () => { setLiveMatchEvent(selectedEvent); setSelectedEvent(null); }
            : undefined}
          isResuming={isGame(selectedEvent.type) ? hasActiveSession(selectedEvent.eventId) : false}
          onReset={isAdmin && isGame(selectedEvent.type) && selectedEvent.played
            ? () => { setSelectedEvent(null); loadEvents(); }
            : undefined}
          hasLiveSession={hasLiveSession}
        />
      )}

      <MatchResultDialog
        open={openMatchResultDialog}
        onClose={() => setOpenMatchResultDialog(false)}
        calendarEvent={matchForResult}
      />

      <Dialog open={openEditDateDialog} onClose={() => setOpenEditDateDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: "primary.main" }} >Aggiorna Data Evento</DialogTitle>
        <DialogContent>
          <TextField
            label="Nuova data :"
            type="date"
            sx={{ mt: 2 }}
            value={dayjs(newDate).format("YYYY-MM-DD")}
            onChange={(e) => setNewDate(new Date(e.target.value))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDateDialog(false)}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveNewDate}
            disabled={!newDate}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <EventEditDialog
        open={openNewEventDialog}
        onClose={() => {
          setOpenNewEventDialog(false);
          setNewEventDate("");
        }}
        onSave={handleCreateEvent}
        initialDate={newEventDate}
        tournamentId={tournamentId}
      />

      <EventEditDialog
        open={openEditEventDialog}
        onClose={() => {
          setOpenEditEventDialog(false);
          setEditingEvent(null);
        }}
        onSave={handleUpdateEvent}
        event={editingEvent}
        tournamentId={tournamentId}
      />

      <LiveMatchDialog
        open={!!liveMatchEvent}
        event={liveMatchEvent}
        onClose={() => setLiveMatchEvent(null)}
        onSaveSuccess={() => { setLiveMatchEvent(null); loadEvents(); }}
      />
    </>
  );
}
